import { createApiClient } from '../oidc-api';
import { 
  ProfileDetails,
  AcceptTermsRequest,
  ProfileCalendar,
  ProfileChatHistory,
  ProfileChat,
  ProfileSchedule,
  HealthActivity,
  HealthCheckpoint,
  HealthDevice,
  GlucoseMeasurement,
  ProgramProgress,
  HealthReport,
  SleepData,
  ActivitySummary
} from './types';

// Profile Details
export const getProfileDetails = async (user: any): Promise<ProfileDetails> => {
  const api = createApiClient(user);
  const response = await api.get<ProfileDetails>('/v1/profile/user_details');
  return response; // The response IS the data, not response.data
};

export const acceptTerms = async (user: any, request: AcceptTermsRequest): Promise<void> => {
  const api = createApiClient(user);
  await api.post('/v1/profile/accept_terms', request);
};

// Calendar
export const getProfileCalendars = async (user: any): Promise<ProfileCalendar[]> => {
  const api = createApiClient(user);
  const response = await api.get<ProfileCalendar[]>('/v1/profile/calendars');
  return response;
};

// Chat
export const getProfileChatHistory = async (user: any): Promise<ProfileChatHistory[]> => {
  const api = createApiClient(user);
  const response = await api.get<ProfileChatHistory[]>('/v1/profile/chats/history');
  return response;
};

export const getProfileChats = async (user: any): Promise<ProfileChat[]> => {
  const api = createApiClient(user);
  const response = await api.get<ProfileChat[]>('/v1/profile/chats');
  return response;
};

// Schedules
export const getProfileSchedules = async (user: any): Promise<ProfileSchedule[]> => {
  const api = createApiClient(user);
  const response = await api.get<ProfileSchedule[]>('/v1/profile/schedules');
  return response;
};

// Health Activities
export const getHealthActivities = async (user: any): Promise<HealthActivity[]> => {
  const api = createApiClient(user);
  const response = await api.get<HealthActivity[]>('/v1/profile/health/activities');
  return response;
};

export const getHealthActivity = async (user: any, activityId: string): Promise<HealthActivity> => {
  const api = createApiClient(user);
  const response = await api.get<HealthActivity>(`/v1/profile/health/activities/${activityId}`);
  return response;
};

// Health Checkpoints
export const getHealthCheckpoints = async (user: any): Promise<HealthCheckpoint[]> => {
  const api = createApiClient(user);
  const response = await api.get<HealthCheckpoint[]>('/v1/profile/health/checkpoints');
  return response;
};

// Health Devices
export const getHealthDevices = async (user: any): Promise<HealthDevice[]> => {
  const api = createApiClient(user);
  const response = await api.get<HealthDevice[]>('/v1/profile/health/devices');
  return response;
};

export const configureHealthDevice = async (user: any, deviceId: string, config: any): Promise<HealthDevice> => {
  const api = createApiClient(user);
  const response = await api.put<HealthDevice>(`/v1/profile/health/devices/${deviceId}/configure`, config);
  return response;
};

export const deactivateHealthDevice = async (user: any, deviceId: string): Promise<void> => {
  const api = createApiClient(user);
  await api.put(`/v1/profile/health/devices/${deviceId}/deactivate`, {});
};

// Glucose Measurements
export const getGlucoseMeasurements = async (user: any): Promise<GlucoseMeasurement[]> => {
  const api = createApiClient(user);
  const response = await api.get<GlucoseMeasurement[]>('/v1/profile/health/glucose/measurements');
  return response;
};

// Program Progress
export const getCurrentProgramProgress = async (user: any): Promise<ProgramProgress> => {
  const api = createApiClient(user);
  const response = await api.get<ProgramProgress>('/v1/profile/health/program/current_program_progress');
  return response;
};

// Health Reports
export const getHealthReports = async (user: any): Promise<HealthReport[]> => {
  const api = createApiClient(user);
  const response = await api.get<HealthReport[]>('/v1/profile/health/reports');
  return response;
};

export const getHealthReport = async (user: any, reportId: string): Promise<HealthReport> => {
  const api = createApiClient(user);
  const response = await api.get<HealthReport>(`/v1/profile/health/reports/${reportId}`);
  return response;
};

// Sleep Data
export const getSleepData = async (user: any): Promise<SleepData[]> => {
  const api = createApiClient(user);
  const response = await api.get<SleepData[]>('/v1/profile/health/sleep');
  return response;
};

export const getSleepDataById = async (user: any, sleepId: string): Promise<SleepData> => {
  const api = createApiClient(user);
  const response = await api.get<SleepData>(`/v1/profile/health/sleep/${sleepId}`);
  return response;
};

// Activity Summary
export const getActivityCountByGroupName = async (user: any): Promise<ActivitySummary[]> => {
  const api = createApiClient(user);
  const response = await api.get<ActivitySummary[]>('/v1/profile/health/summary/activity/count_by_group_name');
  return response;
};